import React from 'react';
import { Field } from 'react-final-form';
import isEqual from 'lodash/isEqual';

import { DisplayCategoryFragment } from '@occc/graphql';
import { useGetDisplayCategoriesLazy } from '@occc-app/dataAccessLayer/displayCategory/queries';
import {
  AutocompleteProps,
  FormInputProps,
  LazyAutocomplete,
  showErrorOnBlurOrDirty,
} from '@occc-app/design-system';

interface DisplayCategorySelectProps extends Pick<AutocompleteProps, 'onFocus' | 'onBlur'> {
  value: DisplayCategoryFragment[];
  onChange: (values: DisplayCategoryFragment[]) => void;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  errorText?: string;
  inputProps?: FormInputProps;
}

const mapQueryResponseToRawArray = (
  response: ReturnType<typeof useGetDisplayCategoriesLazy>['response'],
) => response.data || [];

const mapRawResponseItemToAutocompleteOption = (displayCategory: DisplayCategoryFragment) => ({
  value: displayCategory.id,
  label: displayCategory.name,
  _raw: displayCategory,
});

const DisplayCategorySelect: React.FC<DisplayCategorySelectProps> = (props) => {
  return (
    <LazyAutocomplete<
      DisplayCategoryFragment,
      ReturnType<typeof useGetDisplayCategoriesLazy>['response']
    >
      label='Display Categories'
      {...props}
      useLazyQuery={useGetDisplayCategoriesLazy}
      mapQueryResponseToRawArray={mapQueryResponseToRawArray}
      mapRawResponseItemToAutocompleteOption={mapRawResponseItemToAutocompleteOption}
      placeholder='Start typing to select display categories'
      makeQueryFilter={(search) => ({ search })}
      inputProps={{
        margin: 'none',
      }}
    />
  );
};

interface DisplayCategorySelectFieldProps
  extends Omit<DisplayCategorySelectProps, 'value' | 'onChange'> {
  name: string;
}

export const DisplayCategorySelectField: React.FC<DisplayCategorySelectFieldProps> = (props) => {
  return (
    <Field
      name={props.name}
      isEqual={isEqual}
      render={({ input, meta }) => (
        <DisplayCategorySelect
          {...props}
          value={input.value || []}
          onFocus={input.onFocus}
          onBlur={input.onBlur}
          onChange={input.onChange}
          errorText={showErrorOnBlurOrDirty({ meta }) ? meta.error : undefined}
        />
      )}
    />
  );
};
